// Navbar.jsx
import React, { useState, useRef, useEffect } from 'react';
import { FiSearch, FiUser, FiShoppingCart, FiMenu, FiX } from 'react-icons/fi';
import { MdKeyboardArrowDown, MdKeyboardArrowRight } from 'react-icons/md';
import { FaHeadphonesAlt } from 'react-icons/fa';
import styles from './Navbar.module.css';
import logo from '../Navbar/logo-3_1ea3a52d-3dca-497d-a266-efbad5616c21.webp';
import { Link, useNavigate } from "react-router-dom";
import { useProduct } from '../Context/ProductContext';
import { useAuth } from '../Context/Auth'; // ✅ استيراد Auth Context
import toast from 'react-hot-toast';

export default function Navbar() {
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeMobileDropdown, setActiveMobileDropdown] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const [showUserDropdown, setShowUserDropdown] = useState(false); // ✅ قائمة المستخدم
  const [profileImage, setProfileImage] = useState(null); // ✅ صورة المستخدم
  const searchInputRef = useRef(null);
  const userDropdownRef = useRef(null);
  const fileInputRef = useRef(null); // ✅ مرجع لـ input الملف
  const navigate = useNavigate();
  const { getCartItemsCount, searchTerm, setSearchTerm, initialProducts } = useProduct();
  const { user, isLoggedIn, logoutUser } = useAuth(); // ✅ استخدام Auth

  const cartCount = getCartItemsCount();

  // ✅ تحميل صورة المستخدم من localStorage عند تحميل المكون
  useEffect(() => {
    const storedImage = localStorage.getItem('userAvatar');
    if (storedImage) {
      setProfileImage(storedImage);
    }
  }, []);

  // ✅ معالجة تحميل الصورة
  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfileImage(reader.result);
        localStorage.setItem('userAvatar', reader.result);
        toast.success('Profile image updated!');
      };
      reader.readAsDataURL(file);
    }
  };

  // ✅ دالة لمسح الصورة
  const handleRemoveImage = () => {
    setProfileImage(null);
    localStorage.removeItem('userAvatar');
    toast.success('Profile image removed!');
  };

  const toggleDesktopDropdown = (menu) => {
    setActiveDropdown(activeDropdown === menu ? null : menu);
  };

  const toggleMobileDropdown = (menu) => {
    setActiveMobileDropdown(activeMobileDropdown === menu ? null : menu);
  };

  const handleCartClick = () => {
    navigate('/cart');
  };

  // ✅ التعامل مع أيقونة المستخدم
  const handleUserClick = () => {
    if (isLoggedIn) {
      setShowUserDropdown(!showUserDropdown);
    } else {
      navigate('/login');
    }
  };

  // ✅ تسجيل الخروج
  const handleLogout = () => {
    logoutUser();
    setShowUserDropdown(false);
    toast.success('Logged out successfully!');
    navigate('/');
  };

  // ✅ عند الكتابة في حقل البحث
  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchTerm(value);

    if (value.trim() === '') {
      setShowSearchDropdown(false);
      setSearchResults([]);
    } else {
      const lowercasedValue = value.toLowerCase();
      const results = initialProducts.filter(product =>
        product.name?.toLowerCase().includes(lowercasedValue) ||
        product.category?.toLowerCase().includes(lowercasedValue) ||
        product.description?.toLowerCase().includes(lowercasedValue)
      ).slice(0, 6);

      setSearchResults(results);
      setShowSearchDropdown(true);
    }
  };

  // ✅ عند الضغط على زر البحث أو Enter
  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchTerm.trim() !== '') {
      navigate('/all-products');
      setShowSearchDropdown(false);
    }
  };

  // ✅ عند اختيار منتج من القائمة
  const handleSelectProduct = (productId) => {
    navigate(`/product/${productId}`);
    setShowSearchDropdown(false);
    setSearchTerm('');
  };

  // ✅ إغلاق القوائم عند النقر خارجها
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchInputRef.current && !searchInputRef.current.contains(e.target)) {
        setShowSearchDropdown(false);
      }
      if (userDropdownRef.current && !userDropdownRef.current.contains(e.target)) {
        setShowUserDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className={styles.navbarContainer}>
      {/* 🔝 Top Bar */}
      <div className={styles.topBar}>
        {/* Logo */}
        <div className={styles.logo}>
          <Link to="/">
            <img src={logo} alt="Pesto Logo" />
          </Link>
        </div>

        {/* 🔍 Search Box */}
        <div style={{ position: 'relative', width: '100%' }} ref={searchInputRef}>
          <form className={styles.searchContainer} onSubmit={handleSearchSubmit}>
            <input
              type="text"
              placeholder="Search"
              className={styles.searchInput}
              value={searchTerm}
              onChange={handleSearch}
            />
            <button type="submit" className={styles.searchBtn}>
              <FiSearch size={20} />
            </button>
          </form>

          {/* 🔽 Search Dropdown */}
          {showSearchDropdown && searchResults.length > 0 && (
            <div className={styles.searchDropdown}>
              {searchResults.map(product => (
                <div
                  key={product.id}
                  className={styles.searchResultItem}
                  onClick={() => handleSelectProduct(product.id)}
                >
                  <img src={product.image} alt={product.name} className={styles.searchResultImage} />
                  <div className={styles.searchResultInfo}>
                    <div className={styles.searchResultName}>{product.name}</div>
                    <div className={styles.searchResultCategory}>{product.category}</div>
                    <div className={styles.searchResultPrice}>${product.price}</div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {showSearchDropdown && searchResults.length === 0 && searchTerm.trim() !== '' && (
            <div className={styles.searchDropdown}>
              <div className={styles.noResults}>
                لا توجد نتائج للبحث عن "{searchTerm}"
              </div>
            </div>
          )}
        </div>

        {/* 👤🛒 Icons */}
        <div className={styles.topIcons}>
          {/* ✅ زر المستخدم مع Dropdown */}
          <div style={{ position: 'relative' }} ref={userDropdownRef}>
            <button 
              className={styles.userBtn} 
              onClick={handleUserClick}
              title={isLoggedIn ? user?.FullName || user?.Email : 'Login'}
            >
              <FiUser size={22} />
              {isLoggedIn && (
                <span className={styles.userLoggedIndicator}></span>
              )}
            </button>

            {/* ✅ User Dropdown Menu */}
            {isLoggedIn && showUserDropdown && (
              <div className={styles.userDropdown}>
                <div className={styles.userDropdownHeader}>
                  <div 
                    className={styles.userAvatar}
                    onClick={() => fileInputRef.current?.click()}
                    style={{ cursor: 'pointer', position: 'relative' }}
                    title="Click to upload profile image"
                  >
                    {profileImage ? (
                      <img 
                        src={profileImage} 
                        alt={user?.FullName} 
                        className={styles.avatarImage}
                      />
                    ) : (
                      <span className={styles.avatarPlaceholder}>
                        {(user?.FullName || user?.Email || 'U').charAt(0).toUpperCase()}
                      </span>
                    )}
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    style={{ display: 'none' }}
                  />
                  <div className={styles.userInfo}>
                    <div className={styles.userName}>{user?.FullName || 'User'}</div>
                    <div className={styles.userEmail}>{user?.Email}</div>
                  </div>
                </div>

                <div className={styles.userDropdownDivider}></div>

                <div className={styles.userDropdownLinks}>
                  <Link 
                    to="/profile" 
                    className={styles.userDropdownLink}
                    onClick={() => setShowUserDropdown(false)}
                  >
                    <FiUser size={16} />
                    <span>Profile</span>
                  </Link>
                  <Link 
                    to="/cart" 
                    className={styles.userDropdownLink}
                    onClick={() => setShowUserDropdown(false)}
                  >
                    <FiShoppingCart size={16} />
                    <span>My Orders</span>
                  </Link>
                  {profileImage && (
                    <button 
                      className={`${styles.userDropdownLink} ${styles.removeImageBtn}`}
                      onClick={handleRemoveImage}
                    >
                      <span>🗑️</span>
                      <span>Remove Image</span>
                    </button>
                  )}
                </div>

                <div className={styles.userDropdownDivider}></div>

                <button 
                  className={styles.userDropdownLogout}
                  onClick={handleLogout}
                >
                  <span>🚪</span>
                  <span>Logout</span>
                </button>
              </div>
            )}
          </div>

          <button className={styles.cartBtn} onClick={handleCartClick}>
            <FiShoppingCart size={22} />
            {cartCount > 0 && (
              <span className={styles.cartBadge}>{cartCount}</span>
            )}
          </button>
        </div>

        {/* 🍔 Hamburger Menu Icon for Mobile */}
        <button
          className={styles.hamburgerBtn}
          onClick={() => setIsMenuOpen(!isMenuOpen)}
        >
          <FiMenu size={24} />
        </button>
      </div>

      {/* 🔽 Bottom Navigation (Desktop Only) */}
      <div className={styles.navBar}>
        <div className={styles.navLeft}>
          <Link to="/" className={styles.navLink}>Home</Link>

          <div
            className={styles.navItem}
            onMouseEnter={() => toggleDesktopDropdown('pages')}
            onMouseLeave={() => toggleDesktopDropdown(null)}
          >
            <button className={styles.navLink}>
              Pages
              <MdKeyboardArrowDown size={18} />
            </button>
            {activeDropdown === 'pages' && (
              <div className={styles.dropdown}>
                <Link to="/about">About Us</Link>
                <Link to="/chefs">Pesto's Talented Chefs</Link>
                <Link to="/testimonials">Testimonials</Link>
                <Link to="/contact">Contact Us</Link>
                <Link to="/faqs">Faq's</Link>
              </div>
            )}
          </div>

          <div
            className={styles.navItem}
            onMouseEnter={() => toggleDesktopDropdown('shop')}
            onMouseLeave={() => toggleDesktopDropdown(null)}
          >
            <button className={styles.navLink}>
              Shop
              <MdKeyboardArrowDown size={18} />
            </button>
            {activeDropdown === 'shop' && (
              <div className={styles.dropdown}>
                <Link to="/all-products">All Products</Link>
              </div>
            )}
          </div>

          <div
            className={styles.navItem}
            onMouseEnter={() => toggleDesktopDropdown('blog')}
            onMouseLeave={() => toggleDesktopDropdown(null)}
          >
            <button className={styles.navLink}>
              Blog
              <MdKeyboardArrowDown size={18} />
            </button>
            {activeDropdown === 'blog' && (
              <div className={styles.dropdown}>
                <Link to="/blog">blog</Link>
                <Link to="/our-blog">Our Blog</Link>
              </div>
            )}
          </div>
        </div>

        {/* ☎ Hotline */}
        <div className={styles.hotline}>
          <div className={styles.hotlineIcon}>
            <FaHeadphonesAlt size={24} />
          </div>
          <div className={styles.hotlineInfo}>
            <div className={styles.hotlineLabel}>Hotline Order</div>
            <div className={styles.hotlineNumber}>+01245789300</div>
          </div>
        </div>
      </div>

      {/* 📱 Mobile Menu Overlay */}
      <div
        className={`${styles.mobileMenuOverlay} ${isMenuOpen ? styles.open : ''}`}
        onClick={() => setIsMenuOpen(false)}
      ></div>

      {/* 📱 Mobile Menu Sidebar */}
      <div className={`${styles.mobileMenu} ${isMenuOpen ? styles.open : ''}`}>
        <div className={styles.mobileMenuHeader}>
          <span className={styles.mobileMenuTitle}>Menu</span>
          <button
            className={styles.closeMenuBtn}
            onClick={() => setIsMenuOpen(false)}
          >
            <FiX size={24} />
          </button>
        </div>

        <nav className={styles.mobileNavLinks}>
          <Link
            to="/"
            className={styles.mobileNavLink}
            onClick={() => setIsMenuOpen(false)}
          >
            Home
          </Link>

          {/* Pages Section */}
          <div className={styles.mobileNavLinkItem}>
            <button
              className={styles.mobileNavLink}
              onClick={() => toggleMobileDropdown('pages')}
            >
              Pages
              {activeMobileDropdown === 'pages'
                ? <MdKeyboardArrowDown size={20} />
                : <MdKeyboardArrowRight size={20} />}
            </button>
            <div
              className={`${styles.mobileSubDropdown} ${activeMobileDropdown === 'pages' ? styles.open : ''}`}
            >
              <Link to="/about" onClick={() => setIsMenuOpen(false)}>About Us</Link>
              <Link to="/chefs" onClick={() => setIsMenuOpen(false)}>Pesto's Talented Chefs</Link>
              <Link to="/testimonials" onClick={() => setIsMenuOpen(false)}>Testimonials</Link>
              <Link to="/contact" onClick={() => setIsMenuOpen(false)}>Contact Us</Link>
              <Link to="/faqs" onClick={() => setIsMenuOpen(false)}>Faq's</Link>
            </div>
          </div>

          {/* Shop Section */}
          <div className={styles.mobileNavLinkItem}>
            <button
              className={styles.mobileNavLink}
              onClick={() => toggleMobileDropdown('shop')}
            >
              Shop
              {activeMobileDropdown === 'shop'
                ? <MdKeyboardArrowDown size={20} />
                : <MdKeyboardArrowRight size={20} />}
            </button>
            <div
              className={`${styles.mobileSubDropdown} ${activeMobileDropdown === 'shop' ? styles.open : ''}`}
            >
              <Link to="/all-products" onClick={() => setIsMenuOpen(false)}>
                All Products
              </Link>
            </div>
          </div>

          {/* Blog Section */}
          <div className={styles.mobileNavLinkItem}>
            <button
              className={styles.mobileNavLink}
              onClick={() => toggleMobileDropdown('blog')}
            >
              Blog
              {activeMobileDropdown === 'blog'
                ? <MdKeyboardArrowDown size={20} />
                : <MdKeyboardArrowRight size={20} />}
            </button>
            <div
              className={`${styles.mobileSubDropdown} ${activeMobileDropdown === 'blog' ? styles.open : ''}`}
            >
              <Link to="/blog" onClick={() => setIsMenuOpen(false)}>blog</Link>
              <Link to="//our-blog" onClick={() => setIsMenuOpen(false)}>Our Blog</Link>
            </div>
          </div>
        </nav>

        <div className={styles.mobileMenuBottom}>
          {/* ✅ Mobile User Section
          {isLoggedIn ? (
            <div className={styles.mobileUserSection}>
              <div className={styles.mobileUserInfo}>
                <div className={styles.mobileUserAvatar}>
                  {(user?.FullName || user?.Email || 'U').charAt(0).toUpperCase()}
                </div>
                <div>
                  <div className={styles.mobileUserName}>{user?.FullName || 'User'}</div>
                  <div className={styles.mobileUserEmail}>{user?.Email}</div>
                </div>
              </div>
              <button 
                className={styles.mobileLogoutBtn}
                onClick={() => {
                  handleLogout();
                  setIsMenuOpen(false);
                }}
              >
                Logout
              </button>
            </div>
          ) : (
            <Link 
              to="/login" 
              className={styles.mobileLogin}
              onClick={() => setIsMenuOpen(false)}
            >
              <FiUser size={20} /> Login
            </Link>
          )}
           */}
          <div className={styles.mobileSelector}>
            United States (USD $) <MdKeyboardArrowDown size={18} />
          </div>
        </div>
      </div>
    </div>
  );
}